import React, { useState, useEffect } from 'react';
import { doc, updateDoc, onSnapshot } from '../../lib/auditedFirestore';
import { db } from '../../lib/firebase';
import { getAttemptScores, getEvaluationMark, getQuestionEntry } from '../../utils/examScores';
import StaticSpreadsheetViewer from '../../components/editors/StaticSpreadsheetViewer';

// --- Mascot Component ---
const ScoreMascot = ({ score, total }) => {
    const percentage = total > 0 ? (score / total) * 100 : 0;
    const [isHovered, setIsHovered] = useState(false);

    // Determine Base Emotion based on Score
    let emotion = 'neutral';
    if (percentage >= 80) emotion = 'ecstatic'; // Laughing, bouncing
    else if (percentage >= 60) emotion = 'happy'; // Smiling
    else if (percentage >= 40) emotion = 'neutral'; // Straight face
    else emotion = 'sad'; // Crying

    // Override with Angry on Hover
    const currentEmotion = isHovered ? 'angry' : emotion;

    // Helper: Animation Control
    // We only bounce if ecstatic or happy
    const shouldBounce = !isHovered && (emotion === 'ecstatic' || emotion === 'happy');
    
    // --- SVG Paths & Attributes Logic ---
    
    // 1. Mouth Path
    let mouthD = "M 268 132 Q 275 132 282 132"; // Neutral/Straight
    if (currentEmotion === 'ecstatic' || currentEmotion === 'happy') {
        mouthD = "M 268 128 Q 275 136 282 128"; // Smile
    } else if (currentEmotion === 'sad') {
        mouthD = "M 268 135 Q 275 128 282 135"; // Frown
    } else if (currentEmotion === 'angry') {
        mouthD = "M 268 135 Q 275 128 282 135"; // Angry Frown
    }

    // 2. Eyes Logic
    const showLaughEyes = currentEmotion === 'ecstatic';
    const showNormalEyes = !showLaughEyes;
    
    // 3. Tears Logic
    const showTears = currentEmotion === 'sad';

    // 4. Eyebrows Logic (Only show for angry)
    const showEyebrows = currentEmotion === 'angry';

    // 5. Pupils Rotation (for anger)
    const leftPupilTransform = currentEmotion === 'angry' ? "rotate(-20 265 119)" : "";
    const rightPupilTransform = currentEmotion === 'angry' ? "rotate(20 285 119)" : "";

    return (
        <div 
            className="flex flex-col items-center justify-center mb-6"
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <svg width="180" viewBox="0 0 400 400" xmlns="http://www.w3.org/2000/svg" style={{ filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.05))', cursor: 'pointer' }}>
                <defs>
                    <linearGradient id="logoGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                        <stop offset="0%" style={{ stopColor: '#D93B8A' }} />
                        <stop offset="100%" style={{ stopColor: '#F97A53' }} />
                    </linearGradient>
                </defs>

                {/* Logo Body Line */}
                <path 
                    d="M 125 125 L 200 125 A 25 25 0 0 1 225 150 L 225 275" 
                    fill="none" 
                    stroke="url(#logoGradient)" 
                    strokeWidth="50" 
                    strokeLinecap="round" 
                />
                
                {/* Animated Face Group */}
                <g id="animatedFace">
                    {/* Bounce Animation (CSS-like inline for React) */}
                    {shouldBounce && (
                        <animateTransform 
                            attributeName="transform"
                            type="translate"
                            dur="2s"
                            repeatCount="indefinite"
                            values="0,0; 0,-10; 0,0" 
                            keyTimes="0; 0.5; 1"
                        />
                    )}

                    {/* Face Background */}
                    <circle cx="275" cy="125" r="25" fill="url(#logoGradient)"/>

                    {/* Eyebrows (Angry) */}
                    <path d="M 260 112 L 270 110" stroke="white" strokeWidth="3" strokeLinecap="round" opacity={showEyebrows ? 1 : 0}/>
                    <path d="M 280 110 L 290 112" stroke="white" strokeWidth="3" strokeLinecap="round" opacity={showEyebrows ? 1 : 0}/>

                    {/* Normal Eyes */}
                    <circle cx="265" cy="118" r="4" fill="white" opacity={showNormalEyes ? 1 : 0}/>
                    <circle cx="265" cy="119" r="2" fill="black" opacity={showNormalEyes ? 1 : 0} transform={leftPupilTransform} />

                    <circle cx="285" cy="118" r="4" fill="white" opacity={showNormalEyes ? 1 : 0}/>
                    <circle cx="285" cy="119" r="2" fill="black" opacity={showNormalEyes ? 1 : 0} transform={rightPupilTransform} />

                    {/* Laughing Eyes */}
                    <path d="M 260 120 Q 265 115 270 120" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity={showLaughEyes ? 1 : 0}/>
                    <path d="M 280 120 Q 285 115 290 120" stroke="white" strokeWidth="2" strokeLinecap="round" fill="none" opacity={showLaughEyes ? 1 : 0}/>

                    {/* Mouth */}
                    <path 
                        d={mouthD} 
                        stroke="white" 
                        strokeWidth="2" 
                        fill={currentEmotion === 'ecstatic' ? 'white' : 'none'} 
                        strokeLinecap="round"
                        className="transition-all duration-300 ease-in-out"
                    />
                    
                    {/* Teardrops (Sad) */}
                    <path d="M260,130 C260,135 262,135 265,135 C268,135 270,130 270,125 C270,120 265,120 260,125 Z" fill="lightblue" opacity={showTears ? 1 : 0}>
                        {showTears && <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" />}
                    </path>
                    <path d="M280,130 C280,135 282,135 285,135 C288,135 290,130 290,125 C290,120 285,120 280,125 Z" fill="lightblue" opacity={showTears ? 1 : 0}>
                        {showTears && <animate attributeName="opacity" values="0.4;1;0.4" dur="1.5s" repeatCount="indefinite" begin="0.5s"/>}
                    </path>
                </g>
            </svg>
            <div className="text-center mt-[-40px] ml-16">
                 <span className={`text-sm font-bold px-3 py-1 rounded-full ${
                     emotion === 'ecstatic' ? 'bg-green-100 text-green-700' :
                     emotion === 'happy' ? 'bg-blue-100 text-blue-700' :
                     emotion === 'neutral' ? 'bg-gray-100 text-gray-700' :
                     'bg-red-100 text-red-700'
                 }`}>
                     {emotion === 'ecstatic' ? 'Excellent!' : 
                      emotion === 'happy' ? 'Good Job!' : 
                      emotion === 'neutral' ? 'Keep Going!' : 
                      'Don\'t Give Up!'}
                 </span>
            </div>
        </div>
    );
};

const ExamResult = ({ exam, result: initialResult, onBackToList, setModal }) => {
    const [result, setResult] = useState(initialResult);
    useEffect(() => { setResult(initialResult); }, [initialResult]);
    // Read saved marks only: live updates never trigger another AI valuation.
    useEffect(() => {
        if (!initialResult?.id || typeof onSnapshot !== 'function') return undefined;
        return onSnapshot(doc(db, 'attempts', initialResult.id), snapshot => {
            if (snapshot.exists()) setResult({ ...snapshot.data(), id: snapshot.id || initialResult.id });
        }, error => { console.error('Could not refresh the saved exam result:', error); });
    }, [initialResult?.id]);
    const [isRequesting, setIsRequesting] = useState(false);
    
    // --- State to handle asynchronously loaded questions ---
    const [loadedQuestions, setLoadedQuestions] = useState([]);
    const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
    

    // --- Fetch questions utilizing the Version History Logic ---
    useEffect(() => {
        let active = true;
        setIsLoadingQuestions(true);
        const fetchQuestions = async () => {
            // Prioritize the URL locked in the attempt (result.questionsUrl)
            // Fallback to the currently published exam URL (exam.publishedQuestionsUrl)
            // Fallback to the exam's draft URL (exam.questionsUrl)
            const targetUrl = result?.questionsUrl || exam?.publishedQuestionsUrl || exam?.questionsUrl;

            let fetchedQuestionsArray = [];

            // If legacy questions array exists natively and no targetUrl was specifically locked to this attempt
            if (Array.isArray(result?.examQuestions) && result.examQuestions.length) {
                fetchedQuestionsArray = result.examQuestions;
            } else if (!result?.questionsUrl && exam?.questions && Array.isArray(exam.questions) && exam.questions.length > 0) {
                fetchedQuestionsArray = exam.questions;
            } else if (targetUrl) {
                try {
                    const res = await fetch(targetUrl);
                    if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
                    const data = await res.json();
                    fetchedQuestionsArray = Array.isArray(data) ? data : [];
                } catch (err) {
                    console.error("Failed to fetch questions:", err);
                    if (active && setModal) setModal({ show: true, message: "Failed to load questions for this exam version." });
                }
            } else if (exam?.questions && Array.isArray(exam.questions)) {
                fetchedQuestionsArray = exam.questions;
            }

            if (!active) return;
            setLoadedQuestions(fetchedQuestionsArray);
            setIsLoadingQuestions(false);
        };
        fetchQuestions();
        return () => { active = false; };
    }, [exam, result?.questionsUrl, result?.examQuestions, setModal]);

    const { autoScore, autoTotal, aiScore, aiTotal, totalScore, totalPossible, pendingCount } = getAttemptScores(result);

    // --- VERSIONING DISPLAY TITLE ---
    const displayTitle = result?.examTitle || exam?.publishedTitle || exam?.title || "Exam Results";
    
    // Handle Revaluation Request
    const handleRequestRevaluation = async () => {
        if (!result.id || isRequesting || result.resubmissionStatus === 'revaluation_requested') return;
        setIsRequesting(true);
        try {
            const attemptRef = doc(db, 'attempts', result.id);
            // We set a specific status for revaluation
            await updateDoc(attemptRef, {
                resubmissionStatus: 'revaluation_requested'
            });
            setModal({ show: true, message: "Revaluation request sent to Admin. Your marks will be updated if the request is approved and processed." });
            // Optimistically update result status for UI
            setResult(previous => ({ ...previous, resubmissionStatus: 'revaluation_requested' }));
        } catch (error) {
            console.error("Error requesting revaluation:", error);
            setModal({ show: true, message: "Failed to send request. Please try again." });
        } finally {
            setIsRequesting(false);
        }
    };

    return (
        <div className="bg-white p-6 rounded-lg shadow">
            <h2 className="text-3xl font-bold text-center mb-2">Exam Results</h2>
            <p className="text-center text-xl text-gray-700 mb-6">{displayTitle}</p>

            {/* --- MASCOT INTEGRATION --- */}
            {!pendingCount && <ScoreMascot score={totalScore} total={totalPossible} />}

            {/* Score Display */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {/* Auto Score */}
                <div className="text-center bg-blue-100 p-6 rounded-lg">
                    <p className="text-lg font-semibold text-blue-800">Auto Score</p>
                    <p className="text-4xl font-bold text-blue-600">{autoScore} / {autoTotal}</p>
                    <p className="text-sm text-blue-700">(MCQs, Short Answer, etc.)</p>
                </div>
                
                {/* Descriptive Score */}
                <div className="text-center bg-purple-100 p-6 rounded-lg">
                    <p className="text-lg font-semibold text-purple-800">Descriptive Score</p>
                    <p className="text-4xl font-bold text-purple-600">{aiScore} / {aiTotal}</p>
                     <p className="text-sm text-purple-700">(Spreadsheet and written responses)</p>
                </div>
                
                {/* Total Score */}
                <div className="text-center bg-green-100 p-6 rounded-lg">
                    <p className="text-lg font-semibold text-green-800">Total Marks</p>
                    <p className="text-4xl font-bold text-green-600">{totalScore} / {totalPossible}</p>
                    <p className="text-sm text-green-700">(Overall Score)</p>
                </div>
            </div>

            {pendingCount > 0 && <p role="status" className="mb-6 rounded-lg border border-amber-200 bg-amber-50 p-4 text-amber-900">
                {pendingCount} answer{pendingCount === 1 ? '' : 's'} need{pendingCount === 1 ? 's' : ''} evaluation review. Your answers are saved. The summary includes only confirmed marks and is not final yet. Your administrator can retry the pending evaluation; updated marks will appear here automatically.
            </p>}
            <h3 className="text-2xl font-bold mb-4">Review Answers</h3>
            
            {isLoadingQuestions ? (
                <div className="flex justify-center items-center py-12">
                    <div className="animate-pulse text-purple-600 font-bold tracking-widest uppercase">
                        Loading Questions...
                    </div>
                </div>
            ) : (
                <div className="space-y-6">
                    {loadedQuestions.map((q, index) => {
                        const evaluation = getQuestionEntry(result.evaluations, index, null, q.id);
                        const manualFeedback = result.adminFeedback ? result.adminFeedback[index] : null;
                        const studentAnswerForQ = getQuestionEntry(result.answers, index, null, q.id);

                        return (
                            <div key={index} className="p-4 border rounded-lg">
                                <div className="flex justify-between items-start font-semibold text-lg mb-2">
                                <div className="flex-grow space-y-2">
                                        <p className="font-bold text-gray-800">Question {index + 1}:</p>
                                        
                                        {/* Images */}
                                        {Array.isArray(q.images) && q.images.map((src, idx) => (
                                            <img key={idx} src={src} alt={`Question scenario ${idx + 1}`} className="max-w-md h-auto rounded-lg" />
                                        ))}
                                        
                                        {/* Question Text */}
                                        {q.text && (
                                            <div 
                                                className="text-gray-700 font-normal prose max-w-none"
                                                dangerouslySetInnerHTML={{ __html: q.text }}
                                            />
                                        )}
                                </div>
                                    <span className="text-gray-600 font-medium">{q.marks} Mark(s)</span>
                                </div>

                                {/* --- 1. MCQ Review --- */}
                                {q.type === 'mcq' && (
                                    <div className="mt-2 space-y-2">
                                        {q.options.map((option, oIndex) => {
                                            const isCorrect = (q.correctAnswer || []).includes(oIndex);
                                            const isSelected = (studentAnswerForQ || []).includes(oIndex);
                                            let bgColor = 'bg-gray-50';
                                            if (isCorrect) bgColor = 'bg-green-100 border border-green-300';
                                            if (isSelected && !isCorrect) bgColor = 'bg-red-100 border border-red-300';
                                            return (
                                                <div key={oIndex} className={`p-3 rounded-lg flex items-center ${bgColor}`}>
                                                    <span className="mr-3 text-lg">{isCorrect ? '✅' : isSelected ? '❌' : '⚪'}</span>
                                                    <span>{option}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                {/* --- 2. Short Answer Review --- */}
                                {q.type === 'short-answer' && (
                                    <div className="mt-2 space-y-2">
                                        <div>
                                            <h5 className="font-semibold text-gray-700">Your Answer:</h5>
                                            <p className={`p-2 border rounded ${ (studentAnswerForQ || '').trim().toLowerCase() === (q.correctAnswer || '').trim().toLowerCase() ? 'bg-green-100 border-green-500' : 'bg-red-100 border-red-500'}`}>{studentAnswerForQ || '(No answer)'}</p>
                                        </div>
                                        <div>
                                            <h5 className="font-semibold text-green-700">Correct Answer:</h5>
                                            <p className="p-2 border rounded bg-green-50">{q.correctAnswer}</p>
                                        </div>
                                    </div>
                                )}
                                
                                {/* --- 3. Matching Review --- */}
                                {q.type === 'matching' && (
                                    <div className="mt-2 overflow-x-auto">
                                        <table className="min-w-full text-sm">
                                            <thead className="bg-gray-100">
                                                <tr>
                                                    <th className="p-2 text-left font-semibold">Prompt</th>
                                                    <th className="p-2 text-left font-semibold">Your Match</th>
                                                    <th className="p-2 text-left font-semibold">Correct Match</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {q.prompts.map((prompt, pIndex) => {
                                                    const studentAnswerIndex = (studentAnswerForQ || {})[pIndex];
                                                    const correctAnswerIndex = (q.correctAnswer || {})[pIndex];
                                                    const isCorrect = studentAnswerIndex === correctAnswerIndex;
                                                    return (
                                                        <tr key={pIndex} className={`border-b ${isCorrect ? 'bg-green-50' : 'bg-red-50'}`}>
                                                            <td className="p-2 font-medium">{prompt}</td>
                                                            <td className="p-2">{studentAnswerIndex !== undefined ? q.options[studentAnswerIndex] : '(No match)'}</td>
                                                            <td className="p-2 flex items-center gap-2">
                                                                <span className="font-bold text-gray-700">{correctAnswerIndex !== undefined ? q.options[correctAnswerIndex] : '(N/A)'}</span>
                                                                <span>{isCorrect ? '✅' : '❌'}</span>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                )}

                                {/* --- 4. Case Study Review (FIXED DATA MAPPING) --- */}
                                {q.type === 'case-study' && (
                                    <div className="mt-4 space-y-6">
                                        {/* Loop through Sub-Questions */}
                                        {q.subQuestions && q.subQuestions.map((subQ, subIdx) => {
                                            // Safely retrieve student answer from the remapped answers object
                                            const studentAns = getQuestionEntry(result.answers, index, subIdx, subQ.id);
                                            const subEvaluation = getQuestionEntry(result.evaluations, index, subIdx, subQ.id);

                                            return (
                                                <div key={subIdx} className="ml-0 md:ml-4 border-l-4 border-purple-200 pl-4 py-2">
                                                    <p className="font-bold text-sm text-gray-600 mb-2">
                                                        Sub-Question {subIdx + 1} ({subQ.marks} Marks)
                                                    </p>
                                                    <div className="mb-3 text-gray-800" dangerouslySetInnerHTML={{ __html: subQ.text }} />

                                                    {/* Sub-Question: MCQ */}
                                                    {subQ.type === 'mcq' && (
                                                        <div className="space-y-1">
                                                            {subQ.options.map((opt, oIdx) => {
                                                                const isCorrect = (subQ.correctAnswer || []).includes(oIdx);
                                                                const isSelected = Array.isArray(studentAns) 
                                                                    ? studentAns.includes(oIdx) 
                                                                    : studentAns === oIdx;

                                                                let bg = 'bg-gray-50';
                                                                if (isCorrect) bg = 'bg-green-100 border border-green-300';
                                                                if (isSelected && !isCorrect) bg = 'bg-red-100 border border-red-300';

                                                                return (
                                                                    <div key={oIdx} className={`p-2 rounded flex items-center text-sm ${bg}`}>
                                                                        <span className="mr-2">{isCorrect ? '✅' : isSelected ? '❌' : '⚪'}</span>
                                                                        {opt}
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}

                                                    {/* Sub-Question: Short Answer / Descriptive */}
                                                    {(subQ.type === 'short-answer' || subQ.type === 'descriptive') && (
                                                        <div className="mt-2 text-sm">
                                                            <div className="mb-1">
                                                                <span className="font-bold text-gray-600">Your Answer:</span>
                                                                {subQ.type==='descriptive'?<div className="space-y-3 mt-2">
                                                                    {(typeof studentAns==='string' || studentAns?.wordContent) && <div className="p-2 bg-gray-50 border rounded" dangerouslySetInnerHTML={{__html:typeof studentAns==='string'?studentAns:studentAns.wordContent}}/>}
                                                                    {studentAns?.excelData && <StaticSpreadsheetViewer data={studentAns.excelData}/>}
                                                                    {!studentAns && <p>(No Answer)</p>}
                                                                    {subEvaluation && <div className="p-3 bg-purple-50 border rounded"><b>{subEvaluation.method==='spreadsheet-cells'?'Spreadsheet marking':'Evaluation'}: {getEvaluationMark(subEvaluation) ?? 'Awaiting review'} / {subQ.marks}</b><p className="whitespace-pre-wrap mt-2">{subEvaluation.text}</p></div>}
                                                                </div>:<p className="p-2 bg-gray-50 border rounded text-gray-800">{studentAns || '(No Answer)'}</p>}
                                                            </div>
                                                            {subQ.correctAnswer && (
                                                                <div>
                                                                    <span className="font-bold text-green-600">Correct / Suggested:</span>
                                                                    <p className="p-2 bg-green-50 border border-green-200 rounded text-gray-800">{subQ.correctAnswer}</p>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}

                                                    {/* Sub-Question Explanation */}
                                                    {subQ.explanation && (
                                                        <div className="mt-2 p-2 bg-blue-50 border border-blue-100 rounded text-xs text-blue-800">
                                                            <strong>💡 Explanation:</strong>
                                                            {subQ.explanationFormat === 'image' ? (
                                                                <img src={subQ.explanation} alt="Explanation" className="mt-1 max-w-full h-auto rounded border" />
                                                            ) : (
                                                                <span className="ml-1">{subQ.explanation}</span>
                                                            )}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                                
                                {/* --- 5. Descriptive / General Review --- */}
                                {q.type === 'descriptive' && (
                                    <div className="space-y-4 mt-2">
                                        <div>
                                            <h5 className="font-semibold text-gray-700 mb-2">Your Answer:</h5>
                                            <div className="space-y-4">
                                                {studentAnswerForQ?.wordContent && (
                                                    <div>
                                                        <h6 className="font-semibold text-sm text-gray-600">Word Processor Response</h6>
                                                        <div className="p-2 mt-1 border rounded bg-gray-50 h-64 overflow-y-auto" dangerouslySetInnerHTML={{ __html: studentAnswerForQ.wordContent }}></div>
                                                    </div>
                                                )}
                                                {studentAnswerForQ?.excelData && (
                                                    <div>
                                                        <h6 className="font-semibold text-sm text-gray-600">Spreadsheet Response</h6>
                                                        <div className="mt-1">
                                                            <StaticSpreadsheetViewer data={studentAnswerForQ.excelData} />
                                                        </div>
                                                    </div>
                                                )}
                                                {!studentAnswerForQ?.wordContent && !studentAnswerForQ?.excelData && (
                                                    <p className="p-2 border rounded bg-gray-50">(No answer provided)</p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        <div>
                                            <div className="flex justify-between items-center">
                                                <h5 className="font-semibold text-gray-700">{evaluation?.method==='spreadsheet-cells'?'Spreadsheet marking:':'InstilAI Evaluation:'}</h5>
                                                <span className="font-bold text-lg text-purple-600">
                                                    Mark: {getEvaluationMark(evaluation) ?? 'Awaiting review'} / {q.marks}
                                                </span>
                                            </div>
                                            <div className="p-4 border rounded bg-purple-50 mt-2 min-h-[60px]">
                                                {evaluation ? (
                                                    <p className="text-gray-800 whitespace-pre-wrap">{evaluation.text}</p>
                                                ) : (
                                                    <p className="text-gray-500 italic">InstilAI evaluation is being processed or was not available for this question.</p>
                                                )}
                                            </div>
                                        </div>
                                        
                                        {(q.suggestedAnswerContent || q.suggestedAnswerExcel) && (
                                            <div>
                                                <h5 className="font-semibold text-green-700">Suggested Answer:</h5>
                                                <div className="mt-1 p-4 border rounded bg-green-50 space-y-4">
                                                    
                                                    {/* Display Word/Image Suggested Answer */}
                                                    {q.suggestedAnswerContent && (
                                                        <div>
                                                            <h6 className="font-semibold text-sm text-gray-600 mb-1">Main Answer (Text/Image)</h6>
                                                            {q.suggestedAnswerFormat === 'image' ? (
                                                                <div className="space-y-2">
                                                                    {(Array.isArray(q.suggestedAnswerContent) ? q.suggestedAnswerContent : [q.suggestedAnswerContent]).map((imgSrc, idx) => (
                                                                        <img key={idx} src={imgSrc} alt={`Suggested Answer ${idx + 1}`} className="max-w-full h-auto rounded-lg"/>
                                                                    ))}
                                                                </div>
                                                            ) : (
                                                                <div 
                                                                    className="text-gray-800 overflow-x-auto bg-white p-2 border rounded" 
                                                                    dangerouslySetInnerHTML={{ __html: q.suggestedAnswerContent }} 
                                                                />
                                                            )}
                                                        </div>
                                                    )}

                                                    {q.suggestedAnswerExcel && (
                                                        <div>
                                                            <h6 className="font-semibold text-sm text-gray-600 mb-1">Spreadsheet Answer</h6>
                                                            <div className="bg-white">
                                                                <StaticSpreadsheetViewer data={q.suggestedAnswerExcel} />
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {q.markingSchemeContent && (
                                            <div>
                                                <h5 className="font-semibold text-blue-700">Marking Scheme:</h5>
                                                <div className="mt-1 p-4 border rounded bg-blue-50">
                                                    {q.markingSchemeFormat === 'image' ? (
                                                         <div className="space-y-2">
                                                            {(Array.isArray(q.markingSchemeContent) ? q.markingSchemeContent : [q.markingSchemeContent]).map((imgSrc, idx) => (
                                                                <img key={idx} src={imgSrc} alt={`Marking Scheme ${idx + 1}`} className="max-w-full h-auto rounded-lg"/>
                                                            ))}
                                                        </div>
                                                    ) : (
                                                        <div 
                                                            className="text-gray-800 overflow-x-auto" 
                                                            dangerouslySetInnerHTML={{ __html: q.markingSchemeContent }} 
                                                        />
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}

                                 {/* --- ADMIN MANUAL FEEDBACK DISPLAY --- */}
                                 {manualFeedback && (
                                    <div className="mt-4 p-4 border-2 border-orange-200 rounded-lg bg-orange-50 shadow-sm">
                                        <h5 className="font-bold text-orange-800 text-xs uppercase mb-2 flex items-center gap-2">
                                            <span>✍️</span> Feedback from Instructor
                                        </h5>
                                        <div className="text-gray-800 text-sm whitespace-pre-wrap font-medium">
                                            {manualFeedback}
                                        </div>
                                    </div>
                                )}

                                {/* --- Overall Question Explanation (Applies to all types) --- */}
                                {q.explanation && (
                                    <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
                                        <h5 className="font-bold text-gray-800 mb-2">
                                            {q.type === 'case-study' ? 'Overall Case Logic & Explanation' : 'Explanation'}
                                        </h5>
                                        {q.explanationFormat === 'image' ? (
                                            <img src={q.explanation} alt="Answer Explanation" className="max-w-full h-auto rounded-lg border" />
                                        ) : (
                                            <p className="text-gray-700 whitespace-pre-wrap">{q.explanation}</p>
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}
            
            <div className="mt-8 pt-6 border-t flex flex-col md:flex-row justify-between items-center gap-4">
                <div className="text-gray-700">
                    {result.resubmissionStatus === 'revaluation_requested' && (
                        <p className="font-semibold text-yellow-600 bg-yellow-50 px-4 py-2 rounded-lg border border-yellow-200">
                            Revaluation Requested (Pending Admin Action)
                        </p>
                    )}
                    {result.resubmissionStatus === 'revalued' && (
                        <p className="font-semibold text-green-600 bg-green-50 px-4 py-2 rounded-lg border border-green-200">
                            Revaluation Complete
                        </p>
                    )}
                    {result.resubmissionStatus === 'rejected' && (
                        <p className="font-semibold text-red-600 bg-red-50 px-4 py-2 rounded-lg border border-red-200">
                            Revaluation Request Rejected
                        </p>
                    )}
                    {!result.resubmissionStatus && (
                        <p className="text-sm text-gray-500">Need your answers checked again? Request a revaluation.</p>
                    )}
                </div>

                {!result.resubmissionStatus && (
                    <button 
                        onClick={handleRequestRevaluation} 
                        disabled={isRequesting}
                        className="bg-orange-500 text-white font-bold py-2 px-6 rounded-lg hover:bg-orange-600 disabled:opacity-50 transition"
                    >
                        {isRequesting ? 'Sending Request...' : 'Request Revaluation'}
                    </button>
                )}
            </div>
            
            <button onClick={onBackToList} className="mt-4 w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold py-3 rounded-lg hover:from-purple-700 hover:to-pink-700">Back to Exams</button>
        </div>
    );
};

export default ExamResult;